import { useEffect, useState } from "react";
import { chatApi, imagesApi, imageUrl } from "../api/client";
import type { Chat, GeneratedImage } from "../types";
import "./ImagesView.css";
import { useToast } from "../lib/toast";

export default function ImagesView() {
  const toast = useToast();
  const [images, setImages] = useState<GeneratedImage[]>([]);
  const [chats, setChats] = useState<Chat[]>([]);
  const [chatFilter, setChatFilter] = useState<string>("");
  const [prompt, setPrompt] = useState("");
  const [width, setWidth] = useState(1024);
  const [height, setHeight] = useState(1024);
  const [generating, setGenerating] = useState(false);
  const [selected, setSelected] = useState<GeneratedImage | null>(null);

  const load = async (chatId: string) => {
    try {
      setImages(await imagesApi.list(chatId || undefined));
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  useEffect(() => {
    load(chatFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatFilter]);

  useEffect(() => {
    chatApi.list().then(setChats).catch(() => setChats([]));
  }, []);

  const generate = async () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    try {
      await imagesApi.generate({ prompt, width, height, chat_id: chatFilter || undefined });
      setPrompt("");
      load(chatFilter);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setGenerating(false);
    }
  };

  const deleteImage = async (img: GeneratedImage) => {
    if (!window.confirm("Delete this image?")) return;
    try {
      await imagesApi.delete(img.id);
      setImages((list) => list.filter((i) => i.id !== img.id));
      setSelected((s) => (s?.id === img.id ? null : s));
    } catch (e) {
      toast.error("Failed to delete image: " + (e as Error).message);
    }
  };

  return (
    <div className="view">
      <div className="view-header">
        <div>
          <h2 className="view-title">🖼️ Images</h2>
          <p className="view-subtitle">Generated images gallery (Pollinations.ai)</p>
        </div>
        <label className="row" style={{ gap: 6, fontSize: 12, color: "var(--text-muted)" }}>
          Chat
          <select
            className="select"
            style={{ width: "auto" }}
            value={chatFilter}
            onChange={(e) => setChatFilter(e.target.value)}
          >
            <option value="">All chats</option>
            {chats.map((c) => (
              <option key={c.id} value={c.id}>{c.title}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="card images-generator">
        <textarea
          className="textarea"
          placeholder="Describe the image to generate…"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
        />
        <div className="row" style={{ marginTop: 12, flexWrap: "wrap" }}>
          <label className="row" style={{ gap: 6, fontSize: 12, color: "var(--text-muted)" }}>
            Width
            <input type="number" className="input" style={{ width: 90 }} value={width} onChange={(e) => setWidth(Number(e.target.value))} />
          </label>
          <label className="row" style={{ gap: 6, fontSize: 12, color: "var(--text-muted)" }}>
            Height
            <input type="number" className="input" style={{ width: 90 }} value={height} onChange={(e) => setHeight(Number(e.target.value))} />
          </label>
          <div className="spacer" />
          <button className="btn btn-primary" onClick={generate} disabled={generating}>
            {generating ? <><span className="spinner" /> Generating…</> : "Generate"}
          </button>
        </div>
      </div>

      {images.length === 0 && <div className="empty-state"><h3>No images yet</h3></div>}

      <div className="images-grid">
        {images.map((img) => (
          <div key={img.id} className="image-card" onClick={() => setSelected(img)}>
            <img src={imageUrl(img.file_path)} alt={img.prompt} loading="lazy" />
            <button
              className="btn btn-sm image-card-delete"
              aria-label="Delete image"
              title="Delete image"
              onClick={(e) => { e.stopPropagation(); void deleteImage(img); }}
            >
              🗑
            </button>
            <div className="image-card-overlay">{img.prompt}</div>
          </div>
        ))}
      </div>

      {selected && (
        <div className="image-modal" onClick={() => setSelected(null)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <img src={imageUrl(selected.file_path)} alt={selected.prompt} />
            <div className="image-modal-info">
              <div>{selected.prompt}</div>
              <div className="dim" style={{ fontSize: 12, marginTop: 6 }}>
                {selected.width}×{selected.height}
                {selected.seed ? ` · seed ${selected.seed}` : ""}
              </div>
              <button className="btn btn-danger btn-sm" style={{ marginTop: 8 }} onClick={() => void deleteImage(selected)}>
                Delete
              </button>
            </div>
            <button className="btn btn-sm image-modal-close" onClick={() => setSelected(null)}>×</button>
          </div>
        </div>
      )}
    </div>
  );
}